import React from 'react';

import { ScrollView } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { InnerLayout, LayoutContainer } from 'components/layout/styled';
import { PageAnnouncer } from 'components/page-announcer';
import { ProductCta } from 'components/product-detail-v2/product-cta';
import { ProductDetailHeader } from 'components/product-detail-v2/product-detail-flow/product-detail-header';
import {
  transformComboSlotTypeToEventAttr,
  transformProductWizardStepToEventAttr,
} from 'components/product-detail-v2/utils';
import { MenuObjectTypes } from 'enums/menu';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { ProductWizardStep } from 'state/product-wizard-v2/types';
import { isPicker } from 'utils/menu';

import { ProductCustomizationOptions } from '../product-customization-options';
import { ComboSlotType } from '../types';

import { IProductCustomizationSides } from './types';

export const ProductCustomizationSides: React.FC<IProductCustomizationSides> = ({
  slotKeyToCustomize,
  comboSlot,
  comboSlotOptions,
  currentStep,
  selectedItem,
  handleComboSlotSelectionChange,
  modifierSelections,
  defaultModifiersMap,
  handleChangeModifierSelections,
  pickerAspectExclusions,
  comboSlotType,
  onPressBackNavigation,
  backNavigationLabel = '',
}) => {
  const { formatMessage } = useIntl();
  const { menuObject, updateProductWizardFlowSteps } = useProductWizardContext();

  const isMobile = useIsMobileBreakpoint();

  React.useEffect(() => {
    updateProductWizardFlowSteps({
      id: slotKeyToCustomize,
      type: MenuObjectTypes.COMBO_SLOT,
      currentStep: true,
      comboSlotType: ComboSlotType.SIDES,
    });
  }, [slotKeyToCustomize, updateProductWizardFlowSteps]);

  if (!selectedItem) {
    return null;
  }

  const shouldDisplaySelectedItemNameTitle =
    !isPicker(menuObject) || currentStep === ProductWizardStep.ProductEditOrder;

  const productDetailHeaderTitle =
    shouldDisplaySelectedItemNameTitle && selectedItem
      ? selectedItem.name.locale
      : formatMessage({ id: 'chooseSide' });

  return (
    <>
      <ProductDetailHeader
        title={productDetailHeaderTitle}
        currentStep={ProductWizardStep.ProductCustomization}
        comboSlotType={comboSlotType}
        onPress={onPressBackNavigation}
        backNavigationLabel={backNavigationLabel}
        eventAttributes={{
          name: transformProductWizardStepToEventAttr(ProductWizardStep.ProductCustomization),
          comboSlotType: transformComboSlotTypeToEventAttr(ComboSlotType.SIDES),
        }}
      />

      <ScrollView showsVerticalScrollIndicator>
        <LayoutContainer isFullContainer={isMobile}>
          <InnerLayout>
            <ProductCustomizationOptions
              slotKeyToCustomize={slotKeyToCustomize}
              comboSlot={comboSlot}
              comboSlotOptions={comboSlotOptions}
              selectedItem={selectedItem}
              onSelectComboSlot={handleComboSlotSelectionChange}
              comboSlotType={ComboSlotType.SIDES}
              modifierSelections={modifierSelections}
              modifierDefaults={defaultModifiersMap}
              onModifierSelectionsChange={handleChangeModifierSelections}
              pickerAspectExclusions={pickerAspectExclusions}
            />
          </InnerLayout>
        </LayoutContainer>
      </ScrollView>

      <ProductCta
        currentStep={currentStep}
        menuObject={menuObject}
        modifierSelections={modifierSelections}
        selectedItem={selectedItem}
      />

      <PageAnnouncer pageTitle={productDetailHeaderTitle} />
    </>
  );
};
